// src/components/CompareView.js

import React, { useState, useEffect, useRef } from 'react';
import CompareCell from './CompareCell';

/**
 * CompareView — Product Research
 *
 * The entry is the list chooser: what are you building? A new list
 * (this week, an occasion, a watch) or an existing one. Once a list is
 * chosen, the research opens: the segment selector (opened at the
 * member's timing), the search, the three columns, the anchor/match,
 * and the shortlist that saves into the chosen list.
 */
export default function CompareView({ kinId, categories = [], enabledSegments = [], onSwitchToPortrait, onDraftChanged }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [stores, setStores] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const debounceTimer = useRef(null);

  const [anchor, setAnchor] = useState(null);
  const [matchedProducts, setMatchedProducts] = useState({});
  const [liveShortlist, setLiveShortlist] = useState({});
  const [panelOpen, setPanelOpen] = useState(false);

  // The list chooser (entry) and the segment chooser (in-research).
  const [showListChooser, setShowListChooser] = useState(true);
  const [showSegmentChooser, setShowSegmentChooser] = useState(false);
  const [lists, setLists] = useState([]);
  const [currentList, setCurrentList] = useState(null);
  const [newList, setNewList] = useState({ name: '', type: 'scratch', occasionDate: '', comment: '' });
  const [pendingSegment, setPendingSegment] = useState(null);

  const cellRefs = useRef({});
  const enabledSet = new Set(enabledSegments);

  // Load the member's lists on mount.
  useEffect(() => {
    if (!kinId) return;
    fetch(`/api/lists/my/${kinId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : { lists: [] }))
      .then((d) => setLists(d.lists || []))
      .catch(() => {});
  }, [kinId]);

  const itemSaving = (product) => {
    const avg = product?.stats?.avg30d;
    if (!avg || avg <= product.price) return 0;
    return avg - product.price;
  };

  const shortlistSaving = Object.values(liveShortlist).reduce(
    (sum, { product }) => sum + itemSaving(product),
    0
  );

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query || query.trim().length < 2) {
      setStores(null);
      setAnchor(null);
      setMatchedProducts({});
      return;
    }
    debounceTimer.current = setTimeout(() => fetchCompare(query.trim(), category), 350);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [query, category]);

  const fetchCompare = async (q, cat) => {
    setLoading(true);
    setError(null);
    setAnchor(null);
    setMatchedProducts({});
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ q, limit: '10' });
      if (cat) params.set('category', cat);
      const res = await fetch(`/api/member-drafts/my/${kinId}/compare?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to compare'); }
      const data = await res.json();
      setStores(data.stores || null);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleAnchor = async (productId, chain) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/my/${kinId}/equivalent?productId=${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to look up equivalents');
      const data = await res.json();
      const newAnchor = { productId, chain, equivalents: data.equivalents || {}, hasEquivalence: !!data.hasEquivalence };
      setAnchor(newAnchor);
      const matched = {};
      for (const oc of Object.keys(stores || {})) {
        if (oc === chain) continue;
        const eq = newAnchor.equivalents?.[oc];
        if (eq) matched[oc] = eq.productId;
      }
      setMatchedProducts(matched);
      requestAnimationFrame(() => {
        for (const oc of Object.keys(stores || {})) {
          if (oc === chain) continue;
          const c = cellRefs.current[oc];
          if (!c) continue;
          if (matched[oc]) {
            const el = c.querySelector(`[data-product-id="${matched[oc]}"]`);
            if (el) c.scrollTo({ top: el.offsetTop - c.offsetTop - 8, behavior: 'smooth' });
          } else c.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    } catch (err) { setError(err.message); }
  };

  const handleRelease = () => { setAnchor(null); setMatchedProducts({}); };

  const handleShortlist = (productId, product, chain) => {
    setLiveShortlist((p) => ({ ...p, [productId]: { product, chain } }));
  };

  const handleUnshortlist = (productId) => {
    setLiveShortlist((p) => { const n = { ...p }; delete n[productId]; return n; });
  };

  const saveToCurrentList = async () => {
    const items = Object.values(liveShortlist);
    if (items.length === 0 || !currentList) return false;
    try {
      const token = localStorage.getItem('token');
      for (const { product, chain } of items) {
        await fetch(`/api/shortlist/my/${kinId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            source: 'vhub',
            productId: product.productId,
            supplierId: `vhub:${chain}`,
            name: product.name,
            category: product.category,
            price: product.price,
            imageUrl: product.imageUrl,
            listId: currentList._id,
          }),
        });
      }
      setNotice(`${items.length} item${items.length !== 1 ? 's' : ''} saved to "${currentList.name}"`);
      setLiveShortlist({});
      setPanelOpen(false);
      setTimeout(() => setNotice(null), 3000);
      return true;
    } catch (err) { setError(err.message); return false; }
  };

  const sendToDraft = async () => {
    const items = Object.values(liveShortlist);
    if (items.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      for (const { product } of items) {
        await fetch(`/api/member-drafts/my/${kinId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ productId: product.productId, quantity: 1 }),
        });
      }
      if (onDraftChanged) onDraftChanged();
      setNotice(`${items.length} item${items.length !== 1 ? 's' : ''} sent to draft`);
      setLiveShortlist({});
      setPanelOpen(false);
      setTimeout(() => setNotice(null), 3000);
      return true;
    } catch (err) { setError(err.message); return false; }
  };

  const handleTrash = () => { setLiveShortlist({}); setPanelOpen(false); return true; };

  // --- List chooser ---

  const createList = async () => {
    if (!newList.name.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const body = { name: newList.name.trim(), type: newList.type };
      if (newList.type === 'periodic' && newList.occasionDate) body.occasionDate = newList.occasionDate;
      if (newList.type === 'opportunity' && newList.comment) body.comment = newList.comment;
      const res = await fetch(`/api/lists/my/${kinId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed to create list'); }
      const data = await res.json();
      setCurrentList(data.list);
      setLists((p) => [data.list, ...p]);
      setShowListChooser(false);
      setNewList({ name: '', type: 'scratch', occasionDate: '', comment: '' });
    } catch (err) { setError(err.message); }
  };

  const chooseExistingList = (list) => {
    setCurrentList(list);
    setShowListChooser(false);
  };

  // --- Segment chooser ---

  const requestSegment = (key) => {
    if (key === category) { setShowSegmentChooser(false); return; }
    if (Object.keys(liveShortlist).length > 0) { setPendingSegment(key); return; }
    applySegment(key);
  };

  const applySegment = (key) => {
    setCategory(key);
    setQuery('');
    setStores(null);
    setLiveShortlist({});
    setShowSegmentChooser(false);
    setPendingSegment(null);
    setAnchor(null);
    setMatchedProducts({});
  };

  const disposeAndApply = async (action) => {
    let ok = true;
    if (action === 'list') ok = await saveToCurrentList();
    else if (action === 'draft') ok = await sendToDraft();
    else if (action === 'trash') ok = handleTrash();
    if (ok) applySegment(pendingSegment);
  };

  const columnChains = stores ? Object.keys(stores) : [];
  const shortlistCount = Object.keys(liveShortlist).length;

  // --- The list chooser (entry) ---

  if (showListChooser) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-3 border-b bg-white flex items-center justify-between">
          <div className="text-sm font-semibold">◨ Product Research</div>
          <button
            onClick={onSwitchToPortrait}
            className="px-2 py-2 text-xs font-medium border rounded-lg bg-white text-gray-700 hover:bg-gray-50"
          >
            ◧ Draft Order
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <h2 className="text-lg font-semibold mb-1">What are you building?</h2>
          <p className="text-sm text-gray-500 mb-4">Pick a list, and the research will add to it.</p>

          <div className="space-y-2 mb-6">
            {[
              { key: 'scratch', label: '🛒 This week\'s shop', desc: 'A list for the order you\'re placing now.' },
              { key: 'periodic', label: '🎄 An occasion', desc: 'Christmas, a birthday, the camping trip.' },
              { key: 'opportunity', label: '👀 A watch list', desc: 'Products you\'d buy if the price is right.' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setNewList({ ...newList, type: t.key })}
                className={`w-full text-left border rounded-lg p-3 ${
                  newList.type === t.key ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium">{t.label}</div>
                <div className="text-xs text-gray-500">{t.desc}</div>
              </button>
            ))}
          </div>

          <div className="space-y-2 mb-4">
            <input
              type="text"
              value={newList.name}
              onChange={(e) => setNewList({ ...newList, name: e.target.value })}
              placeholder="List name"
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            {newList.type === 'periodic' && (
              <input
                type="date"
                value={newList.occasionDate}
                onChange={(e) => setNewList({ ...newList, occasionDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            )}
            {newList.type === 'opportunity' && (
              <input
                type="text"
                value={newList.comment}
                onChange={(e) => setNewList({ ...newList, comment: e.target.value })}
                placeholder="Why are you watching? (optional)"
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            )}
            <button
              onClick={createList}
              disabled={!newList.name.trim()}
              className="w-full py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300"
            >
              Create and start researching
            </button>
          </div>

          {lists.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-2">Or add to an existing list:</div>
              <div className="space-y-2">
                {lists.map((l) => (
                  <button
                    key={l._id}
                    onClick={() => chooseExistingList(l)}
                    className="w-full text-left border rounded-lg p-3 hover:bg-gray-50"
                  >
                    <div className="font-medium">{l.name}</div>
                    <div className="text-xs text-gray-500">
                      {l.itemCount} item{l.itemCount !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}
        </div>
      </div>
    );
  }

  // --- The research view (after a list is chosen) ---

  return (
    <div className="flex flex-col h-full relative">
      <div className="p-3 border-b bg-white sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSegmentChooser(true)}
            className="px-2 py-2 text-xs font-medium border rounded-lg bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            {category ? `◨ ${categories.find((c) => c.key === category)?.label || category}` : '◨ Choose segment'}
          </button>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={category ? 'Search in this segment...' : 'Search all products...'}
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={onSwitchToPortrait}
            className="px-2 py-2 text-xs font-medium border rounded-lg bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            ◧ Draft Order
          </button>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="text-[10px] text-gray-500">
            Adding to <span className="font-medium text-gray-700">{currentList?.name}</span>
          </div>
          <button onClick={() => setShowListChooser(true)} className="text-[10px] text-green-700 underline">
            Change list
          </button>
        </div>

        {shortlistCount > 0 && (
          <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between gap-2 flex-wrap">
            <button onClick={() => setPanelOpen(true)} className="text-xs font-medium text-green-800 underline text-left">
              {shortlistCount} shortlisted
              {shortlistSaving > 0 && ` — save $${shortlistSaving.toFixed(2)}`}
              {' — view'}
            </button>
            <div className="flex gap-1">
              <button onClick={saveToCurrentList} className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700">
                Save to list
              </button>
              <button onClick={sendToDraft} className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700">
                Send to draft
              </button>
              <button onClick={handleTrash} className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                Trash
              </button>
            </div>
          </div>
        )}
      </div>

      {notice && <div className="mx-3 mt-2 bg-green-50 border border-green-200 text-green-800 p-2 rounded text-xs">{notice}</div>}
      {error && <div className="mx-3 mt-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

      <div className="flex-1 overflow-hidden">
        {!query || query.trim().length < 2 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm p-6 text-center">
            {category ? 'Search within this segment, or tap the segment button to change.' : 'Choose a segment, or search all products.'}
          </div>
        ) : loading && !stores ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">Comparing...</div>
        ) : columnChains.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm p-6 text-center">
            No products found for "{query}".
          </div>
        ) : (
          <div className="flex h-full">
            {columnChains.map((chain) => {
              const store = stores[chain];
              const isAnchoredColumn = anchor?.chain === chain;
              const hasNoEquivalent = anchor && anchor.chain !== chain && !anchor.equivalents?.[chain];
              return (
                <div key={chain} className="flex-1 min-w-0 border-r last:border-r-0 flex flex-col">
                  <div className="px-2 py-1.5 bg-gray-100 border-b text-center">
                    <div className="text-xs font-semibold text-gray-800 truncate">{store.displayName.split(' — ')[0]}</div>
                    {store.distanceKm != null && <div className="text-[10px] text-gray-500">{store.distanceKm} km</div>}
                  </div>
                  <div ref={(el) => { cellRefs.current[chain] = el; }} className="flex-1 overflow-y-auto p-1.5 space-y-1.5">
                    {hasNoEquivalent && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                        <div className="text-[10px] font-semibold text-amber-800">No equivalent known</div>
                        <div className="text-[9px] text-amber-600 mt-0.5">Scroll to find your own match</div>
                      </div>
                    )}
                    {store.products.length === 0 ? (
                      <div className="text-[10px] text-gray-400 text-center py-4">No matches</div>
                    ) : (
                      store.products.map((p) => (
                        <div key={p.productId} data-product-id={p.productId}>
                          <CompareCell
                            product={p}
                            chain={chain}
                            isShortlisted={!!liveShortlist[p.productId]}
                            isAnchored={isAnchoredColumn && anchor?.productId === p.productId}
                            isMatched={!!matchedProducts[chain] && matchedProducts[chain] === p.productId}
                            onShortlist={handleShortlist}
                            onUnshortlist={handleUnshortlist}
                            onAnchor={handleAnchor}
                            onRelease={handleRelease}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Segment chooser modal */}
      {showSegmentChooser && (
        <div className="absolute inset-0 bg-black bg-opacity-40 z-30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-semibold">Choose a segment</div>
              <button onClick={() => setShowSegmentChooser(false)} className="text-gray-500 hover:text-gray-700 text-lg">✕</button>
            </div>
            <div className="p-3 grid grid-cols-3 gap-2">
              {categories.map((c) => {
                const enabled = enabledSet.has(c.key);
                const current = c.key === category;
                return (
                  <button
                    key={c.key}
                    type="button"
                    disabled={!enabled}
                    onClick={() => requestSegment(c.key)}
                    className={`px-2 py-3 rounded-lg border text-xs font-medium leading-tight ${
                      current ? 'bg-green-600 text-white border-green-600'
                        : enabled ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    {c.label}
                    {!enabled && <div className="text-[9px] font-normal mt-0.5">Off</div>}
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t text-[10px] text-gray-500">
              Segments marked "Off" aren't enabled by your Kin.
            </div>
          </div>
        </div>
      )}

      {/* Disposal gate */}
      {pendingSegment && (
        <div className="absolute inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4">
            <div className="font-semibold mb-1">You have {shortlistCount} item{shortlistCount !== 1 ? 's' : ''} shortlisted</div>
            <div className="text-sm text-gray-600 mb-3">What would you like to do with them before switching segment?</div>
            <div className="flex flex-col gap-2">
              <button onClick={() => disposeAndApply('list')} className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Save to list</button>
              <button onClick={() => disposeAndApply('draft')} className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Send to draft</button>
              <button onClick={() => disposeAndApply('trash')} className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300">Trash</button>
              <button onClick={() => setPendingSegment(null)} className="w-full py-2 text-gray-500 text-sm hover:text-gray-700">Cancel — stay in this segment</button>
            </div>
          </div>
        </div>
      )}

      {/* Shortlist panel */}
      {panelOpen && (
        <div className="absolute inset-0 bg-black bg-opacity-40 z-20 flex items-end">
          <div className="bg-white w-full max-h-[70vh] rounded-t-xl flex flex-col">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-semibold">Shortlist ({shortlistCount})</div>
              <button onClick={() => setPanelOpen(false)} className="text-gray-500 hover:text-gray-700 text-lg">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {Object.entries(liveShortlist).map(([productId, { product, chain }]) => (
                <div key={productId} className="flex items-center justify-between gap-2 border rounded p-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{product.name}</div>
                    <div className="text-xs text-gray-500">
                      {chain} · ${product.price.toFixed(2)}
                      {itemSaving(product) > 0 && (
                        <span className="text-green-700 ml-1">· save ${itemSaving(product).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleUnshortlist(productId)} className="text-xs text-red-600 hover:text-red-800 px-2">Remove</button>
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex gap-2">
              <button onClick={saveToCurrentList} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Save to list</button>
              <button onClick={sendToDraft} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Send to draft</button>
              <button onClick={handleTrash} className="py-2 px-3 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300">Trash</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
